import type { BlogCategory } from '../types/blog';

/**
 * Display labels for each blog category.
 * Single source of truth — imported by:
 *   - pages/BlogCategoryPage.tsx (page H1)
 *   - pages/BlogPostPage.tsx (breadcrumb crumb only — category named there, no separate Badge)
 *   - src/components/blog/CategoryPillNav.tsx (pill label)
 *   - src/components/blog/PostCard.tsx (category Badge on each post tile)
 *
 * To add a category: extend BlogCategory in src/types/blog.ts,
 * add the label here, and add the slug to CATEGORY_ORDER below.
 * TypeScript will surface any consumer that needs updating
 * (Record<BlogCategory, string> enforces exhaustiveness).
 */
export const CATEGORY_LABELS: Record<BlogCategory, string> = {
    'style-guides': 'Style Guides',
    'suburb-guides': 'Suburb Guides',
    'beginners': 'Beginners',
    'platform-news': 'Platform News',
};

/**
 * Display order for categories in navigation.
 * Used by CategoryPillNav to render pills in a consistent sequence.
 * The "All" / "Latest" pill is rendered separately inside CategoryPillNav
 * because it is not a BlogCategory — it is the /blog index route.
 */
export const CATEGORY_ORDER: BlogCategory[] = [
    'style-guides',
    'suburb-guides',
    'beginners',
    'platform-news',
];

/**
 * Client-side pagination batch size for the blog listing pages (/blog and
 * /blog/:category). Single source of truth for both pages. Kept independent from
 * DirectoryPage's GYMS_PER_PAGE per the operator's decision — shared only within
 * the blog module so the two listing pages stay in sync.
 */
export const POSTS_PER_PAGE = 9;

/**
 * Compact page size below the `lg` (1024px) breakpoint. Mobile (1-col) and tablet
 * (2-col) get a shorter initial list + Load-More step so the single/two-column
 * layout isn't a 9-row scroll before the first "Load More"; desktop (3-col) keeps 9.
 * 6 = 3 clean rows on tablet's 2-col grid.
 */
export const POSTS_PER_PAGE_COMPACT = 6;

/**
 * Responsive client-side page size: 9 at `lg`+ (desktop), 6 below (mobile + tablet).
 *
 * Pass `useBreakpointUp('lg')` — which returns the SSR-safe answer on the first
 * render (true at the prerender width, → 9), so the initial slice matches the SSG
 * HTML before the client syncs to the real viewport.
 *
 * ⚠️ Item 1: this took a PIXEL width and compared it to 1024, while the stylesheet
 * switched `lg:` at 64rem — they disagree for a visitor with a raised default font
 * size, so the page size could stop matching the grid the posts were laid out in.
 * Taking the decision rather than the measurement is what stops that recurring: a
 * caller cannot pass the wrong unit to a boolean.
 */
export const postsPerPageFor = (isLgUp: boolean): number =>
    isLgUp ? POSTS_PER_PAGE : POSTS_PER_PAGE_COMPACT;

/**
 * Runtime type-guard for BlogCategory.
 *
 * Single source of truth for blog category route-param validation —
 * consumed by pages/BlogCategoryPage.tsx and pages/BlogPostPage.tsx to
 * narrow useParams output before reading category-dependent state.
 *
 * Co-located with CATEGORY_LABELS / CATEGORY_ORDER so adding a category
 * surfaces every consumer that needs updating in one file.
 */
export const isBlogCategory = (value: string | undefined): value is BlogCategory =>
    value === 'style-guides' ||
    value === 'suburb-guides' ||
    value === 'beginners' ||
    value === 'platform-news';
